import React, { useState, useEffect } from 'react';
import { createAsset } from '../../services/assetService';
import TagService from '../../services/TagService';
import AssetTagService from '../../services/AssetTagService';
import { toast } from 'react-toastify';
import styles from './AssetForm.module.css';
import Select from 'react-select';


const AssetForm = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Tags State
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [qrToken, setQrToken] = useState('');

  useEffect(() => {
    // Check for qrToken in URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('qrToken');
    if (token) {
      setQrToken(token);
    }
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const tags = await TagService.getAllTags();
      const options = tags.map(tag => ({
        value: tag.id,
        label: tag.name,
        color: tag.color
      }));
      setAvailableTags(options);
    } catch (error) {
      console.error("Failed to fetch tags", error);
    }
  };

  /**
   * Handles the form submission.
   * Sends the asset data (name and description) to the server to create a new asset.
   * Then associates any selected tags with the newly created asset.
   * 
   * @param {Event} e - The form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await createAsset({ name, description, qrToken });
      if (response.success) {
        const newAssetId = response.data.id; // Assuming the backend returns the created asset with its ID

        // Save Asset Tags if any are selected
        if (selectedTags.length > 0 && newAssetId) {
          try {
            // Create all associations concurrently
            await Promise.all(
              selectedTags.map(tag =>
                AssetTagService.addAssetTag({
                  asset: { id: newAssetId },
                  tag: { id: tag.value }
                })
              )
            );
          } catch (tagError) {
            console.error("Error associating tags:", tagError);
            toast.warning('Asset created, but some tags failed to link.');
          }
        }

        toast.success('Asset created successfully');
        setName('');
        setDescription('');
        setSelectedTags([]);
      } else {
        toast.error('Failed to create asset');
      }
    } catch (error) {
      toast.error('Error while creating asset');
    }
  };

  // Custom styles for react-select to match HomeBox theme and show Tag Colors
  const customStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: 'var(--input-bg, #fff)',
      borderColor: 'var(--border-color, #ccc)',
      color: 'var(--input-text, #333)',
      minHeight: '42px',
      borderRadius: '8px'
    }),
    multiValue: (styles, { data }) => {
      return {
        ...styles,
        backgroundColor: data.color ? `${data.color}33` : '#3b82f633', // 33 for 20% opacity
        borderRadius: '6px',
        border: `1px solid ${data.color || '#3b82f6'}`
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: 'var(--text-color)',
      fontWeight: '600'
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: data.color || '#3b82f6',
      ':hover': {
        backgroundColor: data.color || '#3b82f6',
        color: 'white',
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'var(--card-bg-color, #fff)',
      zIndex: 999
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected
        ? 'var(--primary-color)'
        : isFocused
          ? 'rgba(59, 130, 246, 0.1)'
          : 'var(--card-bg-color)',
      color: isSelected ? 'white' : 'var(--text-color)'
    })
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Create New Asset</h2>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label htmlFor="name">Name</label>
          <input
            id="name"
            className={styles.input}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="tags">Tags / Collections</label>
          <Select
            isMulti
            name="tags"
            options={availableTags}
            className="basic-multi-select"
            classNamePrefix="select"
            value={selectedTags}
            onChange={setSelectedTags}
            styles={customStyles}
            placeholder="Select tags..."
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            className={styles.input}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </div>

        {qrToken && (
          <div className={styles.formGroup}>
            <label>Linked QR Token</label>
            <input
              className={styles.input}
              type="text"
              value={qrToken}
              readOnly
              style={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}
            />
            <small style={{ color: '#64748b' }}>This asset will be linked to the scanned QR code.</small>
          </div>
        )}

        <button className={styles.submitButton} type="submit">Submit</button>
      </form>
    </div>
  );
};

export default AssetForm;
